// Human-readable labels for an ai-ingest target.
//
// Neither page owns this concept: the task page needs it to label the ingest history on a scope's
// methodology card, and the AI Mapping page needs it for the session picker and the task bar. A copy in
// each would drift the first time a target is added, and the symptom is a raw
// "SCOPE_2_V2_PURCHASED_ELECTRICITY" appearing in one view and not the other.
//
// Its own file under utils/ rather than utils/index.js, which contains JSX in a .js file and is therefore
// unparseable by Vitest — the pattern src/utils/dimensions.js already follows.

export const TARGET_LABELS = {
  SCOPE_1_STATIONARY: "Scope 1 · Stationary",
  SCOPE_1_MOBILE: "Scope 1 · Mobile",
  SCOPE_1_REFRIGERANT: "Scope 1 · Refrigerant",
  SCOPE_2_V1_ELECTRICITY: "Scope 2 · Electricity",
  SCOPE_2_V2_PURCHASED_ELECTRICITY: "Scope 2 · Purchased electricity",
  SCOPE_2_V2_ONSITE_GENERATION: "Scope 2 · On-site generation",
  SCOPE_3_BUSINESS_TRAVEL: "Scope 3 · Business travel",
  SCOPE_3_EMPLOYEE_COMMUTING: "Scope 3 · Employee commuting",
  SCOPE_3_CAPITAL_GOODS: "Scope 3 · Capital goods",
  SCOPE_3_PURCHASED_GOODS: "Scope 3 · Purchased goods & services",
};

/** A target's label, falling back to the raw name so an unmapped target still shows something. */
export function formatTarget(target) {
  if (!target) return "";
  return TARGET_LABELS[target] || target;
}

/**
 * The ingest runs belonging to one methodology scope.
 *
 * Filtered on `methodologyScopeKey`, which the Java DTO already carries — deriving it from the target
 * name here would duplicate `AiIngestTarget.getMethodologyScopeKey()` and drift from it. The methodology
 * card is per scope, and a Scope 3 category card legitimately matches nothing (ai-ingest writes only
 * Scope 1 and Scope 2), which is why an empty result has to be a normal outcome rather than an error.
 */
export function runsForScope(runs, scopeKey) {
  if (!Array.isArray(runs) || !scopeKey) return [];
  return runs.filter((run) => run && run.methodologyScopeKey === scopeKey);
}

/**
 * Ingest runs grouped by the data-mapping session that produced them, keyed on `run.sessionId`.
 *
 * A run's `session_id` is a soft reference the Java side keeps for provenance only (see
 * `AiIngestRun.java`) — a data-mapping session's own status (created/analyzing/mapping/...) never reflects
 * whether it actually committed anything, since committing writes here, not back into that session. This
 * is how the "Your sessions" picker answers "did this one write data into the task" without a backend
 * change: one session can span several commits (one per dataframe), so entries accumulate rather than
 * overwrite.
 */
export function commitsBySessionId(runs) {
  const bySession = {};
  if (!Array.isArray(runs)) return bySession;
  for (const run of runs) {
    if (!run?.sessionId) continue;
    const entry = bySession[run.sessionId] || { count: 0, rowsWritten: 0 };
    entry.count += 1;
    entry.rowsWritten += run.rowsWritten || 0;
    bySession[run.sessionId] = entry;
  }
  return bySession;
}


/**
 * A run's timestamp as a short local date, or "" when it cannot be trusted.
 *
 * Defensive because the wire format is not pinned: `AiIngestRunDto.createdDate` is an `Instant` and the
 * backend sets no explicit Jackson date configuration, so whether it arrives as ISO-8601 or as an epoch
 * number depends on defaults. An ISO string parses correctly; epoch SECONDS misread as milliseconds would
 * parse to 1970, which is worse than showing nothing because it looks like a real answer.
 *
 * So anything that does not land in a plausible range is omitted rather than displayed.
 */
export function formatRunDate(value) {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(value);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return "";
  // 2000-01-01 .. 2200-01-01. Wide enough never to reject a real run, narrow enough to catch a
  // misinterpreted epoch.
  if (ms < 946684800000 || ms > 7258118400000) return "";
  return date.toLocaleDateString();
}
